import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { AlertTriangle, Trash2, HelpCircle } from 'lucide-react';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' = red confirm button + red icon; 'warning' = amber; default = gray */
  variant?: 'danger' | 'warning' | 'default';
}

type ConfirmFn = (options: ConfirmOptions | string) => Promise<boolean>;

const ConfirmDialogContext = createContext<ConfirmFn | null>(null);

interface DialogState extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

export function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
  const [dialog, setDialog] = useState<DialogState | null>(null);

  const confirm = useCallback((optionsOrMessage: ConfirmOptions | string): Promise<boolean> => {
    const options: ConfirmOptions =
      typeof optionsOrMessage === 'string'
        ? { message: optionsOrMessage }
        : optionsOrMessage;

    return new Promise<boolean>((resolve) => {
      setDialog({ ...options, resolve });
    });
  }, []);

  const handleClose = (value: boolean) => {
    dialog?.resolve(value);
    setDialog(null);
  };

  const variant = dialog?.variant ?? 'danger';
  const confirmLabel = dialog?.confirmLabel ?? (variant === 'danger' ? 'Excluir' : 'Confirmar');
  const cancelLabel = dialog?.cancelLabel ?? 'Cancelar';

  const IconEl =
    variant === 'danger' ? Trash2 : variant === 'warning' ? AlertTriangle : HelpCircle;

  const iconBg =
    variant === 'danger'
      ? 'bg-red-100'
      : variant === 'warning'
      ? 'bg-amber-100'
      : 'bg-gray-100';

  const iconColor =
    variant === 'danger'
      ? 'text-red-600'
      : variant === 'warning'
      ? 'text-amber-600'
      : 'text-gray-600';

  const confirmBg =
    variant === 'danger'
      ? 'bg-red-600 hover:bg-red-700'
      : variant === 'warning'
      ? 'bg-amber-500 hover:bg-amber-600'
      : 'bg-gray-800 hover:bg-gray-900';

  return (
    <ConfirmDialogContext.Provider value={confirm}>
      {children}
      {dialog && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="px-6 pt-6 pb-4 flex items-start gap-4">
              <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
                <IconEl className={`h-5 w-5 ${iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                {dialog.title && (
                  <h3 className="text-base font-semibold text-gray-900">{dialog.title}</h3>
                )}
                <p className={`text-sm text-gray-500 ${dialog.title ? 'mt-1' : 'mt-1 font-medium text-gray-800'}`}>
                  {dialog.message}
                </p>
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-3 justify-end">
              <button
                onClick={() => handleClose(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                {cancelLabel}
              </button>
              <button
                onClick={() => handleClose(true)}
                className={`px-4 py-2 text-sm font-medium text-white ${confirmBg} rounded-xl transition-colors`}
              >
                {confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) throw new Error('useConfirm must be used inside ConfirmDialogProvider');
  return ctx;
}
